// Ported from genieos/web/src/components/historian/YAxisScaleChip.tsx @ 84923d036
/**
 * YAxisScaleChip — the per-axis range readout and scaling control (Demsv1.0#2795).
 *
 * Two jobs in one control, deliberately fused:
 *
 *  1. It REPORTS the span currently rendered on this axis. Auto-scaling changes
 *     the vertical gain of the chart without changing anything an operator can
 *     see, so a 0.2 Hz band and a 5 Hz excursion look identical unless the span
 *     is stated. This chip states it. It is the honesty guard on the feature.
 *  2. It SETS the mode for this axis. The chip already names the axis, so it is
 *     the obvious place to change it — a separate popover elsewhere would make
 *     the operator map "left" onto a menu row.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { YAxisScaleMode, YAxisScaleSetting } from './historianTypes';

export interface YAxisScaleChipProps {
  /** Human label for the axis position, e.g. "Left". */
  axisLabel: string;
  /** Colors of the series on this axis, for the leading swatch. */
  colors: string[];
  /** Rendered span text, e.g. "Hz · span 0.250". Absent while unknown. */
  spanLabel?: string;
  setting: YAxisScaleSetting;
  onChange: (setting: YAxisScaleSetting) => void;
}

const MODE_LABEL: Record<YAxisScaleMode, string> = {
  auto: 'Auto',
  zero: 'From zero',
  manual: 'Manual',
};

const MODE_HELP: Record<YAxisScaleMode, string> = {
  auto: 'Fit to the visible data',
  zero: 'Always include zero',
  manual: 'Pin a fixed range',
};

export const YAxisScaleChip: React.FC<YAxisScaleChipProps> = ({
  axisLabel,
  colors,
  spanLabel,
  setting,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Draft state for the manual inputs, so a half-typed "-" or "59." does not
  // get parsed into a bound and snap the axis while the operator is typing.
  const [draftMin, setDraftMin] = useState('');
  const [draftMax, setDraftMax] = useState('');

  useEffect(() => {
    if (setting.mode === 'manual') {
      setDraftMin(setting.min !== undefined ? String(setting.min) : '');
      setDraftMax(setting.max !== undefined ? String(setting.max) : '');
    }
  }, [setting.mode, setting.min, setting.max]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const selectMode = useCallback(
    (mode: YAxisScaleMode) => {
      if (mode === 'manual') {
        onChange({ mode, min: setting.min, max: setting.max });
        return;
      }
      onChange({ mode });
      setOpen(false);
    },
    [onChange, setting.min, setting.max]
  );

  /**
   * Parse a bound, treating blank as ABSENT rather than as a number.
   *
   * `Number('')` is 0 and `Number.isFinite(0)` is true, so an isFinite-only
   * guard silently pins the axis to zero for a field the operator has just
   * cleared — while the chip's own validity message calls that same input
   * invalid. Two code paths disagreeing about one value is worse than either
   * being wrong alone, so both now route through here.
   */
  const parseBound = (raw: string): number | undefined => {
    if (raw.trim() === '') return undefined;
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  };

  /** A range is committable only when both bounds parsed and enclose real height. */
  const validRange = (
    lo: number | undefined,
    hi: number | undefined
  ): lo is number => lo !== undefined && hi !== undefined && lo < hi;

  const commitManual = useCallback(() => {
    const min = parseBound(draftMin);
    const max = parseBound(draftMax);
    // Hold the previous bounds until both fields parse and min < max — an
    // incomplete or inverted range would pin the axis to nonsense.
    if (!validRange(min, max)) return;
    onChange({ mode: 'manual', min: min as number, max: max as number });
  }, [draftMin, draftMax, onChange]);

  const manualInvalid =
    setting.mode === 'manual' &&
    !validRange(parseBound(draftMin), parseBound(draftMax));

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${axisLabel} Y-axis scaling: ${MODE_LABEL[setting.mode]}${
          spanLabel ? `, ${spanLabel}` : ''
        }`}
        className="flex items-center gap-2 rounded-md border border-white/10 bg-white/[0.03]
                   px-2 py-1 text-xs text-ems-text-secondary transition-colors
                   hover:border-white/20 hover:bg-white/[0.06]
                   focus:outline-none focus-visible:ring-1 focus-visible:ring-ems-blue"
      >
        <span className="flex -space-x-1" aria-hidden="true">
          {colors.slice(0, 3).map((c, i) => (
            <span
              key={i}
              className="h-2 w-2 rounded-full ring-1 ring-ems-bg"
              style={{ backgroundColor: c }}
            />
          ))}
        </span>
        <span className="text-ems-text-secondary/70">{axisLabel}</span>
        {spanLabel && (
          <span className="font-mono text-ems-text-primary/90">{spanLabel}</span>
        )}
        <span className="text-ems-text-secondary/50">·</span>
        <span>{MODE_LABEL[setting.mode]}</span>
        <ChevronDown className="h-3 w-3 opacity-60" aria-hidden="true" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 z-20 mt-1 w-56 rounded-md border border-white/10
                     bg-ems-bg-card p-1 shadow-xl"
        >
          {(['auto', 'zero', 'manual'] as YAxisScaleMode[]).map((mode) => (
            <button
              key={mode}
              type="button"
              role="menuitemradio"
              aria-checked={setting.mode === mode}
              onClick={() => selectMode(mode)}
              className={`flex w-full flex-col items-start rounded px-2 py-1.5 text-left text-xs
                          transition-colors hover:bg-white/[0.06] ${
                            setting.mode === mode
                              ? 'text-ems-blue'
                              : 'text-ems-text-primary'
                          }`}
            >
              <span>{MODE_LABEL[mode]}</span>
              <span className="text-[11px] text-ems-text-secondary/70">
                {MODE_HELP[mode]}
              </span>
            </button>
          ))}

          {setting.mode === 'manual' && (
            <div className="mt-1 border-t border-white/10 px-2 pb-1 pt-2">
              <div className="flex items-center gap-2">
                <label className="flex-1">
                  <span className="mb-0.5 block text-[11px] text-ems-text-secondary/70">
                    Min
                  </span>
                  <input
                    type="number"
                    value={draftMin}
                    onChange={(e) => setDraftMin(e.target.value)}
                    onBlur={commitManual}
                    aria-label={`${axisLabel} axis minimum`}
                    className="w-full rounded border border-white/10 bg-white/[0.03] px-1.5 py-1
                               font-mono text-xs text-ems-text-primary
                               focus:border-ems-blue focus:outline-none"
                  />
                </label>
                <label className="flex-1">
                  <span className="mb-0.5 block text-[11px] text-ems-text-secondary/70">
                    Max
                  </span>
                  <input
                    type="number"
                    value={draftMax}
                    onChange={(e) => setDraftMax(e.target.value)}
                    onBlur={commitManual}
                    aria-label={`${axisLabel} axis maximum`}
                    className="w-full rounded border border-white/10 bg-white/[0.03] px-1.5 py-1
                               font-mono text-xs text-ems-text-primary
                               focus:border-ems-blue focus:outline-none"
                  />
                </label>
              </div>
              <p
                className={`mt-1 text-[11px] ${
                  manualInvalid ? 'text-ems-amber' : 'text-ems-text-secondary/50'
                }`}
              >
                {manualInvalid
                  ? 'Enter a min below the max to pin the axis.'
                  : `Pinned ${draftMin}–${draftMax}.`}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default YAxisScaleChip;
